import React, { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  editConnectorRating,
  fetchAllConnectorRatings,
} from '../../../redux/slices/ConnectorRatingSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';

interface EditRatingProperties {
  ratingDetails: any;
  closeModal: () => void;
}

/**
 * Edit Connector Rating side-panel form.
 * Pre-fills all fields from the selected row and submits a PATCH request.
 * @param {EditRatingProperties} param0 - Component props
 * @param {any} param0.ratingDetails - The ratingDetails prop
 * @param {any} param0.closeModal - The closeModal prop
 * @returns {React.JSX.Element} JSX.Element
 */
function EditRating({ ratingDetails, closeModal }: EditRatingProperties) {
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.connectorRating);

  const [editRatingDetails, setEditRatingDetails] = useState({
    name: ratingDetails?.connectorRatingName || ratingDetails?.name || '',
    currentType: ratingDetails?.type || ratingDetails?.currentType || 'AC',
    capacityKW: ratingDetails?.maxPowerOutput || ratingDetails?.capacityKW || '',
    unit: ratingDetails?.unit || 'kW',
    isActive: ratingDetails?.status ?? ratingDetails?.isActive ?? false,
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  /**
   * Handles input and select field changes.
   * @param {string} name - The field name
   * @param {string | number | boolean} value - The new value
   */
  const handleInputChange = (name: string, value: string | number | boolean) => {
    setEditRatingDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Validates and submits the edit connector rating PATCH request.
   * Only changed fields relative to the original record are sent.
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);

    if (!editRatingDetails.name.trim() || !editRatingDetails.capacityKW.toString().trim()) {
      return;
    }

    setDisabled(true);

    try {
      await dispatch(
        editConnectorRating({
          id: ratingDetails.id || ratingDetails._id,
          name: editRatingDetails.name,
          currentType: editRatingDetails.currentType,
          capacityKW: editRatingDetails.capacityKW,
          unit: editRatingDetails.unit,
          isActive: editRatingDetails.isActive,
        }),
      ).unwrap();

      dispatch(
        fetchAllConnectorRatings({
          page,
          limit,
        }),
      );

      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title="Connector Rating Has Been Updated Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              closeModal();
            }}
          />
        ),
      });
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || error?.code || 500,
            message:
              error?.response?.data?.message || error?.message || 'Failed to update connector rating',
          },
        }),
      );
    } finally {
      setDisabled(false);
    }
  };

  return (
    <>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        header={modalView.header}
        content={modalView.content}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
        closeModal={() => setOpenStatus(false)}
      />
      <div className="add-rating-side-panel">
        <div className="panel-header">
          <p>Edit Connector Ratings</p>
        </div>

        <div className="add-rating-scroll-area">
          <div className="add-rating-group">
            <div className="profile-card">
              <div className="edit-fields-container">
                <div className="info-row-input-single">
                  <CustomInput
                    name="name"
                    inputLabel="Rating Name"
                    type="text"
                    value={editRatingDetails.name}
                    placeholder="Enter Rating Name"
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !editRatingDetails.name.trim()}
                    errorMessage="Rating Name is required."
                    characterLimit={100}
                  />
                </div>

                <div className="info-row-input">
                  <SelectField
                    name="currentType"
                    inputLabel="Current Type"
                    selectedValue={editRatingDetails.currentType}
                    placeholder="Select Type"
                    onChangeValue={handleInputChange}
                    options={[
                      { label: 'AC', value: 'AC' },
                      { label: 'DC', value: 'DC' },
                      { label: 'HYBRID', value: 'HYBRID' },
                    ]}
                  />

                  <CustomInput
                    name="capacityKW"
                    inputLabel="Capacity (kW)"
                    type="text"
                    value={editRatingDetails.capacityKW}
                    placeholder="Enter Capacity"
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !editRatingDetails.capacityKW.toString().trim()}
                    errorMessage="Capacity is required."
                    characterLimit={50}
                    svgIcon={
                      <span className="kw-suffix-label">kW</span>
                    }
                  />
                </div>

                <div className="info-row-input-single">
                  <CustomInput
                    name="unit"
                    inputLabel="Unit"
                    type="text"
                    value={editRatingDetails.unit}
                    placeholder="e.g. kW"
                    onChangeValue={handleInputChange}
                    characterLimit={20}
                  />
                </div>

                <div className="checkbox-section">
                  <CheckBoxButtons
                    name="isActive"
                    list={[{ label: 'Mark As Active', value: 'isActive' }]}
                    value={editRatingDetails.isActive ? ['isActive'] : []}
                    onChangeValue={(name: string, value: any) => {
                      handleInputChange(name, value.includes('isActive'));
                    }}
                  />
                  <p className="checkbox-description">
                    Inactive Ratings Will Not Be Available For Selection
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-actions">
            <MyButton
              label="Cancel"
              buttonType="outline"
              onClickFunc={() => {
                closeModal();
              }}
            />

            <MyButton
              disableButton={disabled}
              loading={disabled}
              label="Update"
              buttonType="submit"
              onClickFunc={handleSubmit}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default EditRating;
